import React, { useState, useEffect } from 'react';
import { IconChevronDown, IconChevronUp, IconAlertTriangle } from '../common/AdminIcons.jsx';
import { normalizeLicenseKey } from '../../utils/licenseKeyUtils.js';
import './LicenseKeyEditor.css';

/**
 * ライセンスキー削除確認モーダル
 */
export const LicenseKeyDeleteModal = ({
  open,
  keyName,
  onCancel,
  onConfirm,
  loading,
  error,
  variant = 'registry'
}) => {
  if (!open) return null;

  const isRegistry = variant === 'registry';

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content license-key-delete-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ color: isRegistry ? '#dc2626' : '#374151' }}>
            {isRegistry ? 'ライセンスキーの削除' : '製品からの紐づけ解除'}
          </h2>
          <button type="button" onClick={onCancel} className="modal-close">×</button>
        </div>
        <div className="modal-body">
          <div className="license-key-delete-target">
            <code>{keyName}</code>
          </div>
          {isRegistry ? (
            <div className="license-key-delete-warning">
              <IconAlertTriangle size={20} />
              <span>
                このライセンスキーを削除すると、<strong>すべてのクリニックアカウント</strong>から当該ライセンスが解除され、
                関連する <strong>Stripe サブスクリプションも解約</strong>されます。契約データも削除されます。
                この操作は取り消せません。
              </span>
            </div>
          ) : (
            <p className="license-key-unlink-desc">
              この製品とライセンスキーの紐づけを解除します。ライセンス管理の登録やクリニックの契約には影響しません。
              キーの改名はライセンス管理画面で行ってください。
            </p>
          )}
          {error && <p className="license-key-action-error">{error}</p>}
        </div>
        <div className="modal-footer">
          <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={loading}>
            キャンセル
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={isRegistry ? 'btn btn-danger' : 'btn btn-primary'}
            disabled={loading}
          >
            {loading
              ? (isRegistry ? '削除中...' : '解除中...')
              : (isRegistry ? 'すべてから削除する' : '紐づけを解除')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * 登録済みキー行（製品設定用・セレクトで変更）
 */
export const RegisteredLicenseKeySelectRow = ({
  slotLabel,
  value,
  selectOptions,
  isPrimary,
  canMoveUp,
  canMoveDown,
  onSwap,
  onDelete,
  onMoveUp,
  onMoveDown,
  disabled
}) => {
  const [swapping, setSwapping] = useState(false);

  const handleSelectChange = async (e) => {
    const newKey = e.target.value;
    if (!newKey || newKey === value || swapping) return;
    setSwapping(true);
    try {
      await onSwap(value, newKey);
    } catch {
      e.target.value = value;
    } finally {
      setSwapping(false);
    }
  };

  const options = selectOptions || [value];

  return (
    <div className="license-key-row license-key-row--registered">
      <span className="license-key-slot-label">{slotLabel}</span>
      {isPrimary && <span className="license-key-primary-badge">主キー</span>}
      <select
        className="license-key-select"
        value={value}
        onChange={handleSelectChange}
        disabled={disabled || swapping}
        aria-label={`${slotLabel}のライセンスキー`}
      >
        {options.map((key) => (
          <option key={key} value={key}>{key}</option>
        ))}
      </select>
      <div className="license-key-row-actions">
        <button
          type="button"
          className="license-key-icon-btn"
          onClick={onMoveUp}
          disabled={disabled || swapping || !canMoveUp}
          title="上へ"
          aria-label="上へ"
        >
          <IconChevronUp size={16} />
        </button>
        <button
          type="button"
          className="license-key-icon-btn"
          onClick={onMoveDown}
          disabled={disabled || swapping || !canMoveDown}
          title="下へ"
          aria-label="下へ"
        >
          <IconChevronDown size={16} />
        </button>
        <button
          type="button"
          className="license-key-icon-btn license-key-icon-btn--danger"
          onClick={() => onDelete(value)}
          disabled={disabled || swapping}
          title="紐づけ解除"
          aria-label="紐づけ解除"
        >
          ×
        </button>
      </div>
    </div>
  );
};

/**
 * 登録済みキー行（製品設定用・表示のみ）
 */
export const RegisteredLicenseKeyDisplayRow = ({
  slotLabel,
  value,
  isPrimary,
  canMoveUp,
  canMoveDown,
  onDelete,
  onMoveUp,
  onMoveDown,
  disabled
}) => (
  <div className="license-key-row license-key-row--registered">
    <span className="license-key-slot-label">{slotLabel}</span>
    {isPrimary && <span className="license-key-primary-badge">主キー</span>}
    <code className="license-key-display-value">{value}</code>
    <div className="license-key-row-actions">
      <button
        type="button"
        className="license-key-icon-btn"
        onClick={onMoveUp}
        disabled={disabled || !canMoveUp}
        title="上へ"
        aria-label="上へ"
      >
        <IconChevronUp size={16} />
      </button>
      <button
        type="button"
        className="license-key-icon-btn"
        onClick={onMoveDown}
        disabled={disabled || !canMoveDown}
        title="下へ"
        aria-label="下へ"
      >
        <IconChevronDown size={16} />
      </button>
      <button
        type="button"
        className="license-key-icon-btn license-key-icon-btn--danger"
        onClick={() => onDelete(value)}
        disabled={disabled}
        title="紐づけ解除"
        aria-label="紐づけ解除"
      >
        ×
      </button>
    </div>
  </div>
);

/**
 * 未登録スロット行（ライセンス管理から選択）
 */
export const EmptyLicenseSlotSelectRow = ({
  slotLabel,
  selectOptions,
  onAdd,
  disabled,
  emptyHint = '選択可能なキーがありません'
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [selectedKey, setSelectedKey] = useState('');
  const [adding, setAdding] = useState(false);

  const options = selectOptions || [];
  const canAdd = options.length > 0;

  const handleConfirm = async () => {
    if (!selectedKey) return;
    setAdding(true);
    try {
      await onAdd(selectedKey);
      setSelectedKey('');
      setIsAdding(false);
    } catch {
      /* parent shows error */
    } finally {
      setAdding(false);
    }
  };

  if (isAdding) {
    return (
      <div className="license-key-row license-key-row--adding">
        <span className="license-key-slot-label">{slotLabel}</span>
        {canAdd ? (
          <select
            className="license-key-select"
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
            disabled={adding}
            aria-label="ライセンスキーを選択"
          >
            <option value="">キーを選択...</option>
            {options.map((key) => (
              <option key={key} value={key}>{key}</option>
            ))}
          </select>
        ) : (
          <span className="license-key-placeholder-text">{emptyHint}</span>
        )}
        <div className="license-key-row-actions">
          {canAdd && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleConfirm}
              disabled={adding || !selectedKey}
            >
              追加
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => { setIsAdding(false); setSelectedKey(''); }}
            disabled={adding}
          >
            キャンセル
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="license-key-row license-key-row--empty">
      <span className="license-key-slot-label">{slotLabel}</span>
      <span className="license-key-placeholder-text">未選択</span>
      <button
        type="button"
        className="license-key-add-btn"
        onClick={() => setIsAdding(true)}
        disabled={disabled || !canAdd}
        title={canAdd ? 'ライセンスキーを追加' : emptyHint}
        aria-label="ライセンスキーを追加"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    </div>
  );
};

/**
 * 登録済みライセンスキー行（改名・削除・並べ替え）
 */
export const RegisteredLicenseKeyRow = ({
  slotLabel,
  value,
  originalKey,
  normalizedPreview,
  isPrimary,
  canMoveUp,
  canMoveDown,
  onRename,
  onDelete,
  onMoveUp,
  onMoveDown,
  disabled
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [renaming, setRenaming] = useState(false);

  useEffect(() => {
    setInputValue(value);
  }, [value, originalKey]);

  const preview = normalizeLicenseKey(inputValue);

  const handleBlur = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      setInputValue(originalKey);
      return;
    }
    const normalized = normalizeLicenseKey(trimmed);
    if (normalized === originalKey) return;

    setRenaming(true);
    try {
      await onRename(originalKey, trimmed);
    } catch {
      setInputValue(originalKey);
    } finally {
      setRenaming(false);
    }
  };

  return (
    <div className="license-key-row license-key-row--registered">
      <span className="license-key-slot-label">{slotLabel}</span>
      {isPrimary && <span className="license-key-primary-badge">主キー</span>}
      <input
        type="text"
        className="license-key-input"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleBlur}
        disabled={disabled || renaming}
        placeholder="ライセンスキー"
      />
      {inputValue.trim() && preview !== originalKey && (
        <code className="license-key-preview">→ {preview}</code>
      )}
      <div className="license-key-row-actions">
        <button
          type="button"
          className="license-key-icon-btn"
          onClick={onMoveUp}
          disabled={disabled || !canMoveUp}
          title="上へ"
          aria-label="上へ"
        >
          <IconChevronUp size={16} />
        </button>
        <button
          type="button"
          className="license-key-icon-btn"
          onClick={onMoveDown}
          disabled={disabled || !canMoveDown}
          title="下へ"
          aria-label="下へ"
        >
          <IconChevronDown size={16} />
        </button>
        <button
          type="button"
          className="license-key-icon-btn license-key-icon-btn--danger"
          onClick={() => onDelete(originalKey)}
          disabled={disabled}
          title="削除"
          aria-label="削除"
        >
          ×
        </button>
      </div>
    </div>
  );
};

/**
 * 未登録スロット行（＋で新規入力）
 */
export const EmptyLicenseSlotRow = ({
  slotLabel,
  placeholderText,
  onAdd,
  disabled
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [adding, setAdding] = useState(false);

  const handleConfirm = async () => {
    if (!newValue.trim()) return;
    setAdding(true);
    try {
      await onAdd(newValue.trim());
      setNewValue('');
      setIsAdding(false);
    } catch {
      /* parent shows error */
    } finally {
      setAdding(false);
    }
  };

  if (isAdding) {
    return (
      <div className="license-key-row license-key-row--adding">
        <span className="license-key-slot-label">{slotLabel}</span>
        <input
          type="text"
          className="license-key-input"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder="新しいライセンスキー"
          disabled={adding}
          autoFocus
        />
        <div className="license-key-row-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleConfirm}
            disabled={adding || !newValue.trim()}
          >
            登録
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => { setIsAdding(false); setNewValue(''); }}
            disabled={adding}
          >
            キャンセル
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="license-key-row license-key-row--empty">
      <span className="license-key-slot-label">{slotLabel}</span>
      <span className="license-key-placeholder-text">{placeholderText || '未登録'}</span>
      <button
        type="button"
        className="license-key-add-btn"
        onClick={() => setIsAdding(true)}
        disabled={disabled}
        title="ライセンスキーを追加"
        aria-label="ライセンスキーを追加"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    </div>
  );
};
